// Types shared by the capture worklet, analysis worker, Learn mic capture, the audio lab's engine and
// page, and tests. No DOM access.

export interface PitchEstimate {
  frequency: number; // Hz
  clarity: number; // 0..1, detector-specific confidence (1 = perfectly periodic)
}

export interface TargetEstimate extends PitchEstimate {
  cents: number; // signed distance from the target frequency
  // normalized correlation at double and half the target period, to spot octave confusion
  octaveBelow: number;
  octaveAbove: number;
}

export interface DetectorConfig {
  sampleRate: number;
  windowSize: number; // samples analysed per frame
  hopSize: number; // samples between frames
  minFrequency: number; // Hz
  maxFrequency: number; // Hz
  yinThreshold: number;
  silenceDbfs: number; // frames quieter than this skip the detectors (digital silence fools them)
  enableYin: boolean;
  enableMpm: boolean;
  targetFrequency?: number; // Hz, enables the known-target estimator
  // The app's own echo canceller (issue #3708), run on each window ahead of the detectors. It needs
  // the guide reference, which the capture worklet streams only while told to ('reference' below).
  // expectedDelayMs is a first guess at the guide's delay into the microphone, which narrows the
  // canceller's search to 150 ms either side of it. Neither Learn nor the audio lab passes one: no
  // measurement they have is trustworthy enough (a page session's delay was once 1.07 s against a
  // 412 ms round trip), so both search the whole range. It stays for a caller that measures one.
  canceller?: { enabled: boolean; expectedDelayMs?: number };
}

export const defaultDetectorConfig: DetectorConfig = {
  sampleRate: 48000,
  windowSize: 2048,
  hopSize: 512,
  minFrequency: 70, // just under D2 (73 Hz); a bass fundamental a phone mic can still capture
  maxFrequency: 1100, // just over C6 (1047 Hz); covers children
  yinThreshold: 0.15,
  silenceDbfs: -70,
  enableYin: true,
  enableMpm: true,
};

export interface FrameResult {
  time: number; // AudioContext clock, seconds, at the END of the analysed window
  rms: number;
  dbfs: number;
  yin?: PitchEstimate;
  mpm?: PitchEstimate;
  target?: TargetEstimate;
  // The echo canceller's account of this window, when it is enabled. rms and dbfs above are then the
  // cleaned window's whenever `cancelled`, since that is what the detectors read.
  echo?: EchoReading;
  computeMs: number; // canceller and detector time inside the worker
  cancellerMs?: number;
  yinMs?: number;
  mpmMs?: number;
  targetMs?: number;
  mainThreadLatencyMs?: number; // audio clock at receipt on the main thread minus `time`; set by the engine
  skippedFrames?: number; // older queued frames the worker folded into this one
}

// What the echo canceller made of one window (canceller/echo_canceller.ts), levels in dBFS
export interface EchoReading {
  micDbfs: number; // the raw microphone window
  echoDbfs: number; // the echo estimate subtracted from it; -Infinity when nothing was
  residualDbfs: number; // the cleaned window
  delayMs: number | undefined; // the guide's delay into the microphone, once found
  converged: boolean;
  cancelled: boolean; // false while there is no delay or no reference: the window went through raw
}

// worklet -> worker (or main thread)
export interface FrameMessage {
  type: 'frame';
  samples: Float32Array;
  time: number;
  rms: number;
}

// worklet -> worker (or main thread), every hop while the reference is streamed: the guide reference,
// hopSize samples starting at `time` (AudioContext clock, s), each chunk following the last without a
// gap, posted before the frame of the same hop. Never dropped, unlike frames.
export interface ReferenceMessage {
  type: 'reference';
  samples: Float32Array;
  time: number;
}

// worklet -> main thread
export type WorkletToMainMessage =
  | { type: 'click'; time: number; peak: number; floorRms: number; quantumRms: number }
  // A sung note's start (issue #3723), while onsets are on. time: AudioContext clock, s, at the start
  // of the render quantum the rise was seen in; peakRms: the loudest level just after it.
  | { type: 'onset'; time: number; peakRms: number }
  // time: AudioContext clock, s, at the FIRST sample. reference: the guide reference for exactly the
  // same span (same length, same time), present only when the node was created with a reference
  | { type: 'recordChunk'; samples: Float32Array; time: number; reference?: Float32Array }
  | { type: 'stats'; framesEmitted: number; framesDropped: number; quantaProcessed: number };

// The capture worklet's processorOptions. `reference: true` makes the node's second input the guide
// reference (create the node with numberOfInputs: 2 and connect the guide to input 1); recordings
// then carry it next to the microphone, and the node can stream it to the worker for the canceller.
export interface CaptureProcessorOptions {
  windowSize?: number;
  hopSize?: number;
  reference?: boolean;
}

// main thread -> worklet
export type MainToWorkletMessage =
  | { type: 'port'; port: MessagePort }
  | { type: 'record'; enabled: boolean }
  | { type: 'armClick'; minRms?: number } // detect the next onset that rises well above the noise floor
  | { type: 'onsets'; enabled: boolean } // report every sung onset (onset_detector.ts) until turned off
  | { type: 'configure'; windowSize?: number; hopSize?: number; maxFramesInFlight?: number }
  // stream the guide reference to the worker (a node created with the reference option only)
  | { type: 'reference'; enabled: boolean };

// worker -> worklet over the frame port: the worker is free for the next frame. The worklet
// skips frames while one is in flight, so a slow phone gets fewer results, never stale ones.
export interface AckMessage {
  type: 'ack';
  frames: number; // frames consumed by this analysis (1 plus any older ones skipped)
}

// main thread -> worker
export type MainToWorkerMessage =
  | { type: 'config'; config: Partial<DetectorConfig> }
  | { type: 'port'; port: MessagePort }
  | FrameMessage
  | ReferenceMessage;

// worklet -> worker over the frame port
export type WorkletToWorkerMessage = FrameMessage | ReferenceMessage;

// worker -> main thread
export type WorkerToMainMessage = { type: 'result'; result: FrameResult } | { type: 'ready' };

export const captureProcessorName = 'syp-capture';
